import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Trash2, Loader2, AlertTriangle } from "lucide-react";

export default function MemorialGallery({ applicationId, opportunityId, isAdmin = false }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/memorial-photos", { params: applicationId ? { application_id: applicationId } : { opportunity_id: opportunityId } });
      setPhotos(data.photos || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [applicationId, opportunityId]);
  useEffect(() => { if (applicationId || opportunityId) load(); }, [applicationId, opportunityId, load]);

  const upload = async (file) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("اختر صورة صالحة"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("context", "memorial_photo");
      if (applicationId) fd.append("application_id", applicationId);
      if (opportunityId) fd.append("opportunity_id", opportunityId);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const caption = window.prompt("اكتب تعليقًا للصورة (اختياري):", "") || "";
      await api.post("/memorial-photos", { ...(applicationId ? { application_id: applicationId } : { opportunity_id: opportunityId }), file_id: up.file_id, caption });
      toast.success("تم رفع الصورة التذكارية");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const requestDelete = async (photo) => {
    if (photo.delete_requested_at) { toast.info("طلب الحذف مسجّل مسبقًا وبانتظار اعتماد الأدمن"); return; }
    if (!window.confirm("طلب حذف هذه الصورة؟ الحذف يتطلب اعتماد الأدمن.")) return;
    try {
      await api.post(`/memorial-photos/${photo.id}/request-delete`);
      toast.success("تم إرسال طلب الحذف — بانتظار اعتماد الأدمن");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const adminDelete = async (photo) => {
    if (!window.confirm("حذف نهائي؟ لا يمكن التراجع.")) return;
    try {
      await api.delete(`/memorial-photos/${photo.id}`);
      toast.success("تم الحذف نهائيًا");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5" data-testid="memorial-gallery">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 text-amber-700"><Camera className="h-5 w-5" /></div>
          <div>
            <h3 className="font-heading font-black text-slate-900">لحظات التدريب</h3>
            <div className="text-xs text-slate-500">صور تذكارية يشاركها الطالب والجمعية والمرشد.</div>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 min-h-11 px-4 rounded-xl bg-teal-700 text-white text-sm font-bold cursor-pointer hover:bg-teal-600" data-testid="memorial-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} رفع صورة
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
        </label>
      </div>
      {loading ? (
        <div className="grid place-items-center py-8"><Loader2 className="h-6 w-6 animate-spin text-teal-700" /></div>
      ) : photos.length === 0 ? (
        <div className="rounded-xl bg-slate-50 py-8 text-center text-sm text-slate-500" data-testid="memorial-empty">
          لا توجد صور بعد. كن أول من يشارك لحظة من الرحلة.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {photos.map((p) => (
            <div key={p.id} className="group relative rounded-xl overflow-hidden ring-1 ring-slate-200" data-testid={`memorial-photo-${p.id}`}>
              <img src={p.url} alt={p.caption || "صورة تذكارية من رحلة التدريب"} loading="lazy" className="h-40 w-full object-cover" />
              {p.caption && <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-white text-xs leading-5">{p.caption}</div>}
              <div className="absolute top-2 right-2 flex gap-1">
                {p.delete_requested_at && !isAdmin && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5" title="طلب حذف بانتظار الأدمن">
                    <AlertTriangle className="h-3 w-3" /> بانتظار الحذف
                  </span>
                )}
                {isAdmin ? (
                  <button type="button" onClick={() => adminDelete(p)} className="grid h-8 w-8 place-items-center rounded-full bg-rose-600 text-white opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-700" data-testid={`memorial-admin-delete-${p.id}`} title="حذف نهائي" aria-label="حذف الصورة نهائيًا">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <button type="button" onClick={() => requestDelete(p)} className="grid h-8 w-8 place-items-center rounded-full bg-white/90 text-rose-700 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rose-700" data-testid={`memorial-request-delete-${p.id}`} title="طلب حذف" aria-label="طلب حذف الصورة">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {p.uploaded_by_name && (
                <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/85 backdrop-blur-sm text-[10px] font-bold text-slate-700 px-2 py-0.5">
                  {p.uploaded_by_name}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
